import { AsyncPipe, CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, signal, TemplateRef, viewChild } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { IonIcon } from '@ionic/angular/standalone';
import { BehaviorSubject, combineLatest, EMPTY, from, lastValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { IChatConversation, IChatConversationScope } from 'src/app/model/chat-conversation';
import { IWizardQueryParams } from 'src/app/model/thingie-creation-wizard/wizard-query-parameters';
import { IThingie } from 'src/app/model/thingie/thingie';
import { ICard, StackCardDialogInterface } from 'src/app/model/thingie/thingie-card';
import { GraphAgentContextService } from 'src/app/services/agent/graph-agent-context.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ChatService } from 'src/app/services/chat/chat.service';
import { CopilotSidebarService } from 'src/app/services/copilot-sidebar/copilot-sidebar.service';
import { GraphConfigSeriesDataLoaderService } from 'src/app/services/data/series-data-loader/graph-config-series-data-loader.service';
import { ThingieImportExportService } from 'src/app/services/data/thingie-import-export.service';
import { LogService } from 'src/app/services/loggers/logger.service';
import { PlotlyGeneratorProviderService } from 'src/app/services/plotly-generator/plotly-generator-provider.service';
import { TabStorageService } from 'src/app/services/tab-storage/tab-storage.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { UINotificationService } from 'src/app/services/ui-notification/uinotification.service';
import { UserService } from 'src/app/services/user/user.service';
import { createDefaultTab } from 'src/app/utility/default-object-creator';
import { extractDefaultGraphConfigsFromThingie } from 'src/app/utility/default-tab-helper';
import { TabLoader } from 'src/app/utility/tab-loader';
import { getThingieGroupId, getThingieReplicateIds } from 'src/app/utility/thingie-group-helper';
import { ThingieStatusHelper, ThingieStatusInfo } from 'src/app/utility/thingie-status-helper';
import { defaultThingieComparator } from 'src/app/utility/thingie/default-thingie-comparator';
import { initializeCards } from 'src/app/utility/thingie/thingie-card-generator';
import { DefaultUrls } from 'src/assets/default.urls';
import { SubSink } from 'subsink';
import { PureFunctionCallPipe } from '../../../utility/pipes/pure-member-call/pure-member-call.pipe';
import { ChartViewTabComponent } from '../../chart-view-tab/chart-view-tab.component';
import { AnnotationHandlingDialogComponent, AnnotationHandlingDialogMode } from '../../dialogs/annotation/annotation-handling-dialog/annotation-handling-dialog.component';
import { DialogControl } from '../../dialogs/dialog/dialog-control';
import { DialogSizeMode } from '../../dialogs/dialog/dialog-size-mode.enum';
import { DialogTitleRowExtraContentDirective } from '../../dialogs/dialog/dialog-title-row-extra-content.directive';
import { DialogService, IIsDialog } from '../../dialogs/dialog/dialog.service';
import { GraphConfigurationDialogComponent } from '../../dialogs/graph-configuration-dialog/graph-configuration-dialog.component';
import { StringEllipsisComponent } from '../../string-ellipsis/string-ellipsis.component';
import { TaskStatusBarComponent } from '../../task-status-bar/task-status-bar.component';
import { ThingieStateChipComponent } from '../../thingie-state-chip/thingie-state-chip.component';
import { ThingieCardListComponent } from '../thingie-card-list/thingie-card-list.component';

@Component({
  selector: 'app-stack-card-dialog',
  templateUrl: './stack-card-dialog.component.html',
  styleUrls: ['../thingie-card-view/thingie-card-view.component.scss'],
  styles: [
    ' :host { display: block; height: 100%;}',
    '.copilot-history-trigger { overflow: visible; }',
    '.copilot-history-badge { display: inline-flex; }',
    '.copilot-history-badge .mat-badge-content { top: 0.4rem !important; right: -0.1rem !important; }',
    '.copilot-history-menu { min-width: 18rem; max-width: 24rem; padding: 0.25rem 0; }',
    '.copilot-history-menu__empty { padding: 0.75rem 1rem; color: var(--mat-sys-on-surface-variant, rgba(0, 0, 0, 0.6)); font-size: 0.875rem; }',
    '.copilot-history-menu__section-label { padding: 0.25rem 1rem 0.5rem; color: var(--mat-sys-on-surface-variant, rgba(0, 0, 0, 0.6)); font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }',
    '.copilot-history-item { line-height: 1.25; }',
    '.copilot-history-item__content { display: flex; min-width: 0; flex-direction: column; gap: 0.125rem; }',
    '.copilot-history-item__title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }',
    '.copilot-history-item__meta { color: var(--mat-sys-on-surface-variant, rgba(0, 0, 0, 0.6)); font-size: 0.75rem; }'
  ],
  imports: [
    MaterialImportsModule,
    CommonModule,
    ThingieStateChipComponent,
    NgClass,
    IonIcon,
    NgTemplateOutlet,
    ChartViewTabComponent,
    ThingieCardListComponent,
    TaskStatusBarComponent,
    AsyncPipe,
    PureFunctionCallPipe,
    StringEllipsisComponent,
    DialogTitleRowExtraContentDirective
  ]
})
export class StackCardDialogComponent implements OnDestroy, AfterViewInit, IIsDialog<StackCardDialogInterface, 'noNavigation' | undefined> {
  public readonly __compileTimeTypeInferenceHelper: [StackCardDialogInterface, 'noNavigation' | undefined][] = [];
  protected readonly urls = DefaultUrls;
  private readonly relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  protected readonly recentCopilotConversationLimit = 5;

  private taskStatusBarButton = viewChild.required('stackedModeTaskStatusBarButton',
    { read: TemplateRef });

  private subscriptions = new SubSink();
  protected dialogParams!: StackCardDialogInterface;
  protected graphTabRevision = signal(0);
  protected recentCopilotConversations$!: Observable<IChatConversation[]>;

  /**
   * @private
   */
  protected _graphsChartIsLoadedTrigger$ = new BehaviorSubject<boolean>(false);

  /**
   * Resolved from `dialogParams.stackedCard.thingieIds`
   */
  private thingieIdListFromCard$ = new ReplaySubject<string[]>(1);

  /**
   * @private
   *
   * Starts out with an empty list and is sorted by default comparator
   */
  protected _sortedResolvedThingiesFromCardThingieIdList$ = this
    .thingieIdListFromCard$
    .pipe(
      startWith([]),
      switchMap(ids => {
        if (ids.length === 0) {
          return of([]);
        }

        return this.getThingiesByIds(ids);
      }),
      map(thingies => thingies.sort(defaultThingieComparator)),
      tap(thingies => {
        // update the archived status info
        const numberOfArchivedThingies = thingies.filter(thingie => thingie.archived).length;
        this.archivedStatusInfo = undefined;
        if (numberOfArchivedThingies > 0) {
          this.archivedStatusInfo = ThingieStatusHelper.getArchivedStatusInfo();
          if (thingies.length === numberOfArchivedThingies) {
            this.archivedStatusInfo.description = [{ text: `This experiment is archived.` }];
          } else {
            this.archivedStatusInfo.status = `${numberOfArchivedThingies}/${thingies.length} Archived`;
            this.archivedStatusInfo.description = [{ text: `${numberOfArchivedThingies}/${thingies.length} replicates are archived.` }];
          }
        }
      })
    );

  protected isEditor = signal<boolean>(false);

  public title$: Observable<string>;

  protected data: StackCardDialogInterface;

  protected archivedStatusInfo: ThingieStatusInfo | undefined = undefined;

  constructor(
    private thingieService: ThingieService,
    private router: Router,
    private userService: UserService,
    private dialogService: DialogService,
    private log: LogService,
    private titleService: Title,
    private tabStorage: TabStorageService,
    private uiNotifications: UINotificationService,
    private chatService: ChatService,
    private copilotSidebarService: CopilotSidebarService,
    private graphAgentContextService: GraphAgentContextService,
    private thingieExportService: ThingieImportExportService,
    private dataLoader: GraphConfigSeriesDataLoaderService,
    private generatorProvider: PlotlyGeneratorProviderService,
    private dialogControl: DialogControl<StackCardDialogInterface, 'noNavigation' | undefined>,
    private auth: AuthService,
  ) {
    this.dialogParams = this.data = dialogControl.data;

    this.title$ = this.dialogParams.stackedCard.thingie$.pipe(
      map(thingie => thingie.baseName),
      startWith('Experiment')
    );

    this.thingieIdListFromCard$.next(this.dialogParams.stackedCard.thingieIds);
    this.recentCopilotConversations$ = this.thingieIdListFromCard$
      .pipe(
        switchMap(thingieIds => {
          if (thingieIds.length === 0) {
            return of([]);
          }

          const scope: IChatConversationScope = {
            scopeKind: 'experiment',
            linkedThingieIds: thingieIds,
            contextLabel: this.dialogParams.thingies[0]?.baseName ?? 'Experiment',
            sharingMode: 'replicate_access'
          };

          return this.chatService.getConversations(scope);
        }),
        map(conversations => conversations.slice(0, this.recentCopilotConversationLimit)),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // Subscribe to router events
    this.subscriptions.sink = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter(() => this._graphsChartIsLoadedTrigger$.getValue() === true),
      )
      .subscribe((event: NavigationEnd) => {
        this._checkToOpenGraphConfigurationDialog(event.urlAfterRedirects);
      });


    // listen to chart to open the graph dialog
    this.subscriptions.sink = this._graphsChartIsLoadedTrigger$.pipe(
      filter((isOpen) => isOpen === true),
      first(),
      tap(() => {
        this._checkToOpenGraphConfigurationDialog(this.router.url);
      })
    ).subscribe();

    void this.auth.hasEditorRoleInProject(this.data.stackedCard.project).then(isEditor => {
      this.isEditor.set(isEditor);
    });
  }

  public ngAfterViewInit(): void {
    this.subscriptions.sink = this.dialogParams.stackedCard.thingie$.subscribe(thingie => {
      this.titleService.setTitle(`${thingie.baseName}`);
    });

    this.dialogControl.setActionButtonsTemplate(this.taskStatusBarButton());
  }

  protected _graphsChartIsLoaded() {
    this._graphsChartIsLoadedTrigger$.next(true);
  }

  private _checkToOpenGraphConfigurationDialog(_url: string) {
    if (_url.includes('graph-configuration')) {
      void this._openConfigureGraphDialog(this.dialogParams.stackedCard);
    }
  }

  protected getThingiesByIds = (ids: string[]): Observable<IThingie[]> => {
    if (ids.length === 0) {
      return of([]);
    }
    return combineLatest(ids.map(id => this.thingieService.getThingieById(id)));
  };

  protected routeToReplicate(url: string) {
    void this.router.navigate([url]);
    void this.dialogControl.requestCloseDialog('noNavigation');
  }

  protected changePopupMode(data: StackCardDialogInterface, event: MatButtonToggleChange) {
    this.dialogControl.setSizeMode(event.value === 'stacked' ? DialogSizeMode.Maximize : DialogSizeMode.ShrinkToContent);

    if (event.value === 'stacked') {
      document.querySelector('.stack-dialog-98e3-c268c62c1213')?.classList.remove('individual-cards');
      data.popupMode = 'stacked';
      initializeCards([data.stackedCard], this.tabStorage, this.dataLoader, this.generatorProvider, this.subscriptions);
    } else {
      document.querySelector('.stack-dialog-98e3-c268c62c1213')?.classList.add('individual-cards');
      data.popupMode = 'single';
      initializeCards(data.singleCards, this.tabStorage, this.dataLoader, this.generatorProvider, this.subscriptions);
    }
  }

  protected async removePinnedCard(thingieId: string, card: ICard) {
    const relatives: string[] = [];

    (await lastValueFrom(this.thingieService.getThingieById(thingieId).pipe(take(1))
      .pipe(map(thingie => {
        thingie.relations.forEach(relation => {
          if (relation.type === 'experimentGroup') {
            relatives.push(relation.relatedThingie);
          }
        });
      }))))!;

    relatives.push(thingieId);

    this.userService.unpinThingies(relatives);

    const thingieString: string = relatives.join(':');

    void this.userService.removePinnedCard(thingieString);

    card.isPinned = false;
  }

  protected async addPinnedCard(thingieId: string, card: ICard) {
    const relatives: string[] = [];

    (await lastValueFrom(this.thingieService.getThingieById(thingieId).pipe(take(1))
      .pipe(map(thingie => {
        thingie.relations.forEach(relation => {
          if (relation.type === 'experimentGroup') {
            relatives.push(relation.relatedThingie);
          }
        });
      }))))!;

    relatives.push(thingieId);

    this.userService.pinThingies(relatives);

    const thingieString: string = relatives.join(':');

    void this.userService.addPinnedCard(thingieString);

    card.isPinned = true;
  }

  protected _addAnnotation(thingieIds: string[]) {
    if (!this.dialogParams) {
      return;
    }
    const thingies = this.dialogParams.thingies.filter(t => thingieIds.includes(t._id!));

    void this.dialogService.open({
      component: AnnotationHandlingDialogComponent,
      data: {
        mode: AnnotationHandlingDialogMode.Create,
        defaultThingie: null,
        siblingThingies: thingies,
        projectId: this.dialogParams.stackedCard.project,
      }
    });
  }

  protected async _openCopilot(): Promise<void> {
    const thingies = this.dialogParams.thingies;
    if (thingies.length === 0) {
      return;
    }

    const firstThingie = thingies[0];
    const linkedThingieIds = thingies
      .map(thingie => thingie._id)
      .filter((thingieId): thingieId is string => thingieId !== undefined);
    const scope: IChatConversationScope = {
      scopeKind: 'experiment',
      linkedThingieIds,
      experimentGroupId: getThingieGroupId(linkedThingieIds),
      contextLabel: firstThingie.baseName,
      sharingMode: 'replicate_access'
    };
    const hiddenContext = await this.graphAgentContextService.buildHiddenContextForThingie(
      firstThingie,
      this.dialogParams.chartViewTabs?.toArray().map(tab => tab.tab) ?? [],
      { linkedThingieIds }
    );
    this.copilotSidebarService.openScopedConversation(scope, { hiddenContext });
  }

  protected getConversationBadgeLabel(conversationCount: number): string {
    if (conversationCount <= 0) {
      return '';
    }

    return conversationCount > 9
      ? '9+'
      : `${conversationCount}`;
  }

  protected formatConversationUpdatedAt(updatedAt: string | undefined): string {
    if (!updatedAt) {
      return 'Recently active';
    }

    const timestamp = new Date(updatedAt).getTime();
    if (Number.isNaN(timestamp)) {
      return 'Recently active';
    }

    const diffInSeconds = Math.round((timestamp - Date.now()) / 1000);
    const absoluteSeconds = Math.abs(diffInSeconds);

    if (absoluteSeconds < 60) {
      return this.relativeTimeFormatter.format(diffInSeconds, 'second');
    }

    const diffInMinutes = Math.round(diffInSeconds / 60);
    if (Math.abs(diffInMinutes) < 60) {
      return this.relativeTimeFormatter.format(diffInMinutes, 'minute');
    }

    const diffInHours = Math.round(diffInMinutes / 60);
    if (Math.abs(diffInHours) < 24) {
      return this.relativeTimeFormatter.format(diffInHours, 'hour');
    }

    const diffInDays = Math.round(diffInHours / 24);
    if (Math.abs(diffInDays) < 7) {
      return this.relativeTimeFormatter.format(diffInDays, 'day');
    }

    const diffInWeeks = Math.round(diffInDays / 7);
    if (Math.abs(diffInWeeks) < 5) {
      return this.relativeTimeFormatter.format(diffInWeeks, 'week');
    }

    const diffInMonths = Math.round(diffInDays / 30);
    if (Math.abs(diffInMonths) < 12) {
      return this.relativeTimeFormatter.format(diffInMonths, 'month');
    }

    const diffInYears = Math.round(diffInDays / 365);
    return this.relativeTimeFormatter.format(diffInYears, 'year');
  }

  protected openCopilotConversation(conversationId: string): void {
    this.copilotSidebarService.openConversation(conversationId);
  }

  protected async _openGraphConfiguration() {
    if (this.router.url.includes(DefaultUrls.route.dashboardThingies)) {
      void this._openConfigureGraphDialog(this.dialogParams.stackedCard);
      return;
    }

    void this.router.navigate([DefaultUrls.route.dashboardStackGraphConfiguration(this.dialogParams.stackedCard.thingieIds[0])]);
  }

  private async _openConfigureGraphDialog(card: ICard) {
    const tab = card.popupTab;
    const thingie = (await lastValueFrom(card.thingie$.pipe(take(1))))!;

    const dialog = this.dialogService.open({
      component: GraphConfigurationDialogComponent,
      data: {
        thingie,
        tabs: [tab],
        selectedTabIndex: 0,
        createNewTab: false,
        isDashboardCard: true,
      },
      sizeMode: DialogSizeMode.Maximize,
    });

    const result = await dialog.afterClosed();

    if (result !== undefined) {
      result.tabs.forEach((t) => {
        if (t.backendId === card.popupTab.backendId) {
          card.popupTab = t;
        }
      });

      card.popupTab.graphConfig = result.tabs[0].graphConfig;
      card.previewTab.graphConfig = result.tabs[0].graphConfig;
      this.graphTabRevision.set(this.graphTabRevision() + 1);

      this.dialogParams?.chartViewTabs?.forEach(tab => {
        void tab.refreshGraph();
      });
    }

    if (!this.router.url.includes(DefaultUrls.route.dashboardThingies)) {
      void this.router.navigate([DefaultUrls.route.dashboardStackDetail(card.thingieIds[0])]);
    }
  }

  protected excelExport(thingieIds: string[]) {
    if (thingieIds.length === 0) {
      this.log.warn('Trying to show export dialog for 0 objects. Not showing dialog.');
      return;
    }

    let thingies: IThingie[];
    this.subscriptions.sink = combineLatest(thingieIds.map(id => this.thingieService.getThingieById(id)))
      .pipe(
        first(),
        tap(list => thingies = list),
        // Load tabs for each thingie
        switchMap(
          thingies => combineLatest(
            thingies.map(
              t => this.tabStorage.loadCurrentUserTabConfigurations(
                { thingieId: t._id!, dashboardCard: false }
              ).then(async result => {
                // Merge the resulting tab list (if any) with the tab of the dashboard card

                let finalList = [...result ?? []];
                const dashboardCard = await TabLoader.getTabForDashboardCard(
                  this.tabStorage,
                  of(thingies[0]),
                  getThingieGroupId(getThingieReplicateIds(thingies[0])),
                  thingieIds,
                  false
                );

                if (dashboardCard) {
                  finalList.push(dashboardCard);
                }

                if (finalList.length === 0 || (finalList.length === 1 && finalList[0]._isDashboardCard)) {
                  const defaultConfigs = await extractDefaultGraphConfigsFromThingie(t);
                  finalList = defaultConfigs.map(g => ({
                    ...createDefaultTab(),
                    _isDashboardCard: g.isDashboardConfig,
                    graphConfig: g
                  }));
                }

                return finalList;
              })
            )
          )
        ),
        switchMap(potentialTabsList => {
          const replicateSelections = potentialTabsList.map((tabs, idx) => ({
            thingie: thingies[idx],
            graphSelections: tabs.map(tab => ({
              graphConfig: {
                ...tab.graphConfig,
                isDashboardConfig: undefined
              },
              selected: false,
              isDashboardCard: tab._isDashboardCard
            }))
          }));

          return from(this.thingieExportService.showExportDialog({
            levelMode: 'replicate',
            canSwitchLevelMode: false,
            title: `Export\u00a0of\u00a0"${thingies[0].baseName}"`,
            experiments: [
              {
                experimentName: thingies[0].baseName,
                replicateSelections
              }
            ]
          }));
        }),
        catchError((err: unknown) => {
          this.log.error('Export dialog encountered error', err);
          return EMPTY;
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected async updateArchiveProperty(thingieCard: ICard, state: 'archive' | 'unarchive') {
    const promises = thingieCard.thingieIds.map(async thingieId => {
      if (state === 'archive') {
        await this.thingieService.archiveThingie(thingieId);
      } else {
        await this.thingieService.unarchiveThingie(thingieId);
      }
    });

    await Promise.all(promises);

    let tempMsg = state === 'archive' ? 'Replicate successfully archived' : 'Replicate successfully unarchived';

    if (promises.length > 1) {
      tempMsg = state === 'archive' ? `${promises.length} replicates successfully archived` :
        `${promises.length} replicates successfully unarchived`;
    }

    this.uiNotifications.displaySnackbar({
      message: tempMsg
    });
  }

  /**
   * @private
   * @param thingie
   */
  protected _duplicateExperiment(thingie: IThingie) {
    const queryParams: IWizardQueryParams = {
      mode: 'DUPLICATE',
      duplicateReferenceOnly: false,
      referenceId: thingie._id!
    };

    void this.dialogControl.requestCloseDialog('noNavigation');

    void this.router.navigate(
      [DefaultUrls.route.thingieCreate],
      { queryParams }
    );
  }

  /**
   * @private
   * @param thingie
   */
  protected _editExperiment(thingie: IThingie) {
    const queryParams: IWizardQueryParams = {
      mode: 'EDIT',
      navigationSourceTitle: 'Dashboard',
      navigationSourceUrl: this.router.url,
      referenceId: thingie._id!
    };

    void this.dialogControl.requestCloseDialog('noNavigation');

    void this.router.navigate(
      [DefaultUrls.route.thingieCreate],
      { queryParams }
    );
  }


  /**
   * Closes the dialog according to the popup mode
   *
   * @private
   */
  protected async _closeDialog() {
    if (this.dialogParams.popupMode === 'single') {
      await this.dialogControl.requestCloseDialog('noNavigation');
    } else {
      await this.dialogControl.requestCloseDialog();
    }
  }
}
